import { useEffect } from 'react';

// Visibility-gated polling heartbeat. Fires `refresh` immediately when
// the tab becomes visible and every `intervalMs` while it stays
// visible; pauses entirely while the tab is hidden so a backgrounded
// pin doesn't hammer the API. No-op when `active` is false.
//
// `refresh` is captured per effect run — pass a stable identity
// (useCallback) or accept that an identity change tears the timer down
// and restarts it. Errors thrown by `refresh` are swallowed; the next
// tick re-tries. SSR-safe (the effect doesn't fire server-side).
export function usePollingHeartbeat(active, refresh, intervalMs = 120000) {
  useEffect(() => {
    if (!active || typeof refresh !== 'function') return undefined;
    let timer = null;
    const tick = () => {
      if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
      try {
        const result = refresh();
        if (result && typeof result.then === 'function') {
          result.catch(() => {
            /* swallow — next tick retries */
          });
        }
      } catch {
        /* swallow — next tick retries */
      }
    };
    const start = () => {
      if (timer) return;
      timer = setInterval(tick, intervalMs);
    };
    const stop = () => {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        tick();
        start();
      } else {
        stop();
      }
    };
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') start();
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVis);
    return () => {
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVis);
      stop();
    };
  }, [active, refresh, intervalMs]);
}
